import * as Highcharts from 'highcharts';

import {AfterViewInit, Component, Inject, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {CockpitCard} from 'shared/dtos/cockpit-card';
import {SeriesAbsolutPerc} from 'shared/dtos/cockpit/charts/seriesAbsolutPerc';
import {Cockpit} from 'shared/dtos/cockpit/cockpit';
import {Lohnsumme} from 'shared/dtos/cockpit/lohnsumme';
import {
  ABSOLUT,
  ANTEIL,
  GESTAPELT,
  UNGESTAPELT
} from 'shared/EnumsConstants/card-charts-translate-keys.enum';
import {DataStorageService} from 'shared/services/data-storage.service';
import {TableExportService} from 'shared/services/table-data-export/table-data-export.service';
import {ChartTemplateService} from '../../charts-templates/chart-template.service';
import {CockpitContentComponent} from '../../cockpit-content-component';
import {CurrentLocaleComponent} from 'shared/components/current-locale-component';

@Component({
  selector: 'app-personalaufwand',
  templateUrl: './personalaufwand.component.html',
  styleUrl: '../../card/card.component.scss',
  providers: [ChartTemplateService],
  standalone: false
})
export class ChartDialogPersonalaufwandComponent
  extends CurrentLocaleComponent
  implements OnInit, AfterViewInit, CockpitContentComponent
{
  card: CockpitCard;
  chart: Highcharts.Chart;

  isAbsolut = true;
  stacked = false;
  lohnsumme: Lohnsumme;
  series: SeriesAbsolutPerc;
  crtSeries: string;
  gestapelt: string = GESTAPELT;
  ungestapelt: string = UNGESTAPELT;
  absolut: string = ABSOLUT;
  anteil: string = ANTEIL;

  constructor(
    @Inject(ChartTemplateService)
    private readonly chartTemplate: ChartTemplateService,
    private readonly storage: DataStorageService,
    protected readonly translate: TranslateService,
    private readonly tableExport: TableExportService
  ) {
    super(translate);
    this.lohnsumme = new Lohnsumme();
  }

  async ngOnInit() {
    super.ngOnInit();
    this.chartTemplate.chartname = 'personalaufwand';

    this.storage.getCockpitData().then(data => {
      this.lohnsumme = (data as Cockpit).lohnsumme;
    });
  }

  ngAfterViewInit() {
    this.storage.getChartsData().then(data => {
      this.series = data.lohnsumme;

      this.initHighCharts(JSON.parse(this.series.seriesAbsolute));
      this.toggleDisplayChartSerie(this.chart);
    });

    this.translate.onLangChange.subscribe(() => {
      const svgContainer = document.getElementById(
        'svgContainerChartDialogPersonalaufwand'
      );
      if (svgContainer) {
        this.initHighCharts(JSON.parse(this.crtSeries));
        this.toggleDisplayChartSerie(this.chart);
      }
    });
  }

  exportChart() {
    this.chart.exportChart(
      {
        type: 'application/pdf',
        filename: 'my-pdf'
      },
      {}
    );
  }

  exportTable() {
    const cardTitle = this.translate.instant(
      'i18n.cockpit.card.Personalaufwand.title'
    );
    this.tableExport.exportHtmlTable(
      null,
      cardTitle,
      'i18n.cockpit.card.chart.personalaufwand.lohnsummeNachGeschlecht'
    );
  }

  selectionAbsolutAnteilChanged(item) {
    if (item.value === ABSOLUT) {
      this.isAbsolut = true;
    } else {
      this.isAbsolut = false;
    }
    this.toggleDisplayChartSerie(this.chart);
  }

  selectionGestapeltUngestepeltChanged(item) {
    if (item.value === GESTAPELT) {
      this.stacked = true;
    } else {
      this.stacked = false;
    }
    this.toggleDisplayChartSerie(this.chart);
  }

  private toggleDisplayChartSerie(chart: Highcharts.Chart) {
    this.intitializeChart();
    chart = this.chart;

    if (this.isAbsolut) {
      this.crtSeries = this.series.seriesAbsolute;
    } else {
      this.crtSeries = this.series.seriesPercent;
    }

    if (!chart.series) {
      this.initHighCharts(JSON.parse(this.crtSeries));
    }

    this.setDisplayInfoStackedCharts(chart);
  }

  private setDisplayInfoStackedCharts(chart: Highcharts.Chart) {
    let plotOptions = {};
    const title = this.isAbsolut
      ? this.translate.instant(
          'i18n.cockpit.card.chart.personalaufwand.lohnsummeImCHF'
        )
      : this.translate.instant(
          'i18n.cockpit.card.chart.personalaufwand.lohnsummeImPerzent'
        );

    const pointFormat = this.isAbsolut
      ? '<span style="color:{series.color}">{series.name}</span>: <span style="font-weight:bold;">{point.y}</span> CHF<br/>'
      : '<span style="color:{series.color}">{series.name}</span>: <span style="font-weight:bold;">{point.y}</span> %<br/>';

    const valueDecimals = this.isAbsolut ? 0 : 1;
    const tooltip = {
      pointFormat,
      valueDecimals
    };

    const max = this.isAbsolut ? null : 100;
    const labels = this.isAbsolut ? null : '{value}%';
    const ticks = this.isAbsolut ? null : 10;

    const yAxis = {
      min: 0,
      max,
      title: {text: ''},
      labels: {format: labels},
      tickInterval: ticks
    };

    const series = this.crtSeries
      ? JSON.parse(this.chartTemplate.translateChart(this.crtSeries))
      : chart.options.series;

    if (this.stacked) {
      plotOptions = {
        plotOptions: {
          series: {stacking: 'normal'}
        },
        title: {
          text: title,
          align: 'center',
          floating: false,
          style: {color: '#333333', fontSize: '16px'},
          useHTML: true
        },
        series,
        tooltip,
        yAxis
      } as Highcharts.Options;
    } else {
      plotOptions = {
        plotOptions: {
          series: {stacking: ''}
        },
        title: {
          text: title,
          align: 'center',
          floating: false,
          style: {color: '#333333', fontSize: '16px'}
        },
        series,
        tooltip,
        yAxis
      };
    }
    chart.update(plotOptions, false);
    chart.reflow();
    chart.redraw();
  }

  private initHighCharts(series: string) {
    const xAxis = {
      categories: [
        this.translate.instant(
          'i18n.cockpit.card.chart.personalaufwand.standardisiert'
        ),
        this.translate.instant(
          'i18n.cockpit.card.chart.personalaufwand.effektiv'
        )
      ]
    };
    const chartTitle = {
      text: this.translate.instant(
        'i18n.cockpit.card.chart.personalaufwand.lohnsummeImCHF'
      ),
      style: {color: '#333333', fontSize: '16px'}
    };
    const yAxis = {
      labelsFormat: null,
      min: 0,
      max: null,
      tickInterval: null,
      titleText: ''
    };
    const legend = {reversed: true};
    const tooltip = {
      pointFormat:
        '<span style="color:{series.color}">{series.name}</span>: <span style="font-weight:bold;">{point.y}</span> CHF<br/>',
      valueDecimals: 0
    };

    this.chartTemplate.initializeChartValues(
      series,
      chartTitle,
      xAxis,
      yAxis,
      legend,
      'normal',
      tooltip
    );

    this.intitializeChart();
  }

  private intitializeChart() {
    Highcharts.chart(
      'svgContainerChartDialogPersonalaufwand',
      this.chartTemplate.json,
      thechart => {
        this.callBackFuncToNormalizeBarChart(thechart);

        // Observer pour détecter les changements de taille du conteneur
        const resizeObserver = new ResizeObserver(() => {
          if (this.chart) {
            this.chart.reflow();
          }
        });
        resizeObserver.observe(
          document.getElementById('svgContainerChartDialogPersonalaufwand')
        );
      }
    );
  }

  private callBackFuncToNormalizeBarChart(thechart: Highcharts.Chart) {
    this.chart = thechart;
    thechart.reflow();
  }
}
